export const ROUTE_PATHS = {
  HOME: '/',
  LEARN: '/learn',
  REVIEW: '/review',
  PHONICS: '/phonics',
  PROGRESS: '/progress',
  PARENT: '/parent',
  REWARDS: '/rewards',
} as const;

export interface WordEntry {
  id: string;
  emoji: string;
  zh: string;
  pinyin?: string;
  jp: string;
  kana?: string;
  en: string;
  category: string;
  isChar?: boolean;
}

export interface ChildProfile {
  id: string;
  name: string;
  age: number;
  avatar: string;
}

export interface WordProgress {
  wordId: string;
  masteryZh: 0 | 1 | 2;
  masteryJp: 0 | 1 | 2;
  masteryEn: 0 | 1 | 2;
  nextDueDate: string;
  reviewCount: number;
}

export interface Reward {
  id: string;
  name: string;
  emoji: string;
  pointsCost: number;
  createdAt: string;
}

export interface AppStorage {
  wordProgress: Record<string, WordProgress>;
  customWords: WordEntry[];
  rewards: Reward[];
  lastStudyDate: string | null;
  streak: number;
  todayUnlocked: boolean;
  todayWordIds: string[];
  points: number;
  dailyWordCount: number;
  dailyPhonicsCount: number;
}

export const POINTS_CONFIG = {
  mastery: 5,
  practice: 3,
  cheer: 0,
  dailyComplete: 20,
  streakBonus: 15,
} as const;

export const DEFAULT_STORAGE: AppStorage = {
  wordProgress: {},
  customWords: [],
  rewards: [],
  lastStudyDate: null,
  streak: 0,
  todayUnlocked: false,
  todayWordIds: [],
  points: 0,
  dailyWordCount: 3,
  dailyPhonicsCount: 10,
};

const EDGE_TTS_VOICE_MAP = {
  zh: 'zh-CN-XiaoxiaoNeural',
  jp: 'ja-JP-NanamiNeural',
  en: 'en-US-JennyNeural',
};

const BROWSER_TTS_LANG_MAP = {
  zh: 'zh-CN',
  jp: 'ja-JP',
  en: 'en-US',
};

let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioContext;
}

async function playAudioBuffer(arrayBuffer: ArrayBuffer): Promise<void> {
  const ctx = getAudioContext();
  const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
  const source = ctx.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(ctx.destination);
  source.start(0);
}

function fallbackToSpeechSynthesis(text: string, lang: 'zh' | 'jp' | 'en'): void {
  if ('speechSynthesis' in window) {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = BROWSER_TTS_LANG_MAP[lang];
    utterance.rate = 0.8;
    utterance.pitch = 1.1;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  }
}

export async function speakWord(
  text: string,
  lang: 'zh' | 'jp' | 'en',
  speakCharOverride?: string
): Promise<void> {
  const textToSpeak = speakCharOverride ?? text;

  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (supabaseUrl && supabaseKey && !supabaseUrl.includes('placeholder')) {
      const response = await fetch(`${supabaseUrl}/functions/v1/edge-tts-proxy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
          'apikey': supabaseKey,
        },
        body: JSON.stringify({ text: textToSpeak, lang }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.audio) {
          // Base64-encoded MP3
          const binaryString = atob(data.audio);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          await playAudioBuffer(bytes.buffer);
          return;
        }
        // fallback flag or missing audio -> use browser TTS
      }
    }
    throw new Error('Edge TTS unavailable');
  } catch (_error) {
    fallbackToSpeechSynthesis(textToSpeak, lang);
  }
}

export function getNextReviewWords(storage: AppStorage, allWords: WordEntry[]): WordEntry[] {
  const today = new Date().toISOString().split('T')[0];
  const dueWords: WordEntry[] = [];

  for (const word of allWords) {
    const progress = storage.wordProgress[word.id];
    if (!progress) continue;

    if (progress.nextDueDate <= today) {
      dueWords.push(word);
    }
  }

  return dueWords.sort((a, b) => {
    const progressA = storage.wordProgress[a.id];
    const progressB = storage.wordProgress[b.id];
    const avgMasteryA = (progressA.masteryZh + progressA.masteryJp + progressA.masteryEn) / 3;
    const avgMasteryB = (progressB.masteryZh + progressB.masteryJp + progressB.masteryEn) / 3;
    return avgMasteryA - avgMasteryB;
  });
}
